import PlanForm from "features/settings/Subscription/PlanForm";
import React, { useContext, useLayoutEffect, useMemo } from "react";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context";
import useMedia from "utils/hooks/useMedia";
import {
  LOCAL_STORAGE_FAILED_PAYMENT_KEY,
  SMALL_SCREEN,
} from "common/constants";
import { displayResultMessage } from "utils/misc";
import { SubscriptionPaymentMethod } from "@shared/features/settings/subscription/types";
import useStepWithSummary from "features/settings/Subscription/useStepWithSummary";
import { sharedLib } from "@shared/services/shared.service";
import { scrollToTop, useVirtualKeyboard } from "utils/hooks/misc";
import Summary from "features/settings/Subscription/Summary";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay.tsx";
import {
  FormInnerWrapper,
  FormWithSummaryWrapper,
  FormWrapper,
  SummaryInnerWrapper,
  SummaryWrapper,
} from "features/settings/common/styles.ts";
import styled, { useTheme } from "styled-components";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants";
import { telegramOpenLink } from "features/telegram/utils.ts";
import NewLookCheckoutLayout, {
  NewLookMobileCheckout,
} from "features/settings/Subscription/NewLook/NewLookCheckoutLayout.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

const StepWithSummary = () => {
  const {
    isBottomSummary,
    subscriptionPaymentMethod,
    isFetchingPaymentMethods,
    isCryptoPayment,
    isPromoCodeApplied,
    plan,
    previousStep,
  } = useContext(BuySubscriptionContext)!;
  const { isNewLook } = useTheme();
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const isSmallScreen = useMedia(
    [`(max-width: ${SMALL_SCREEN})`],
    [true],
    false
  );
  const { isKeyboardOpen } = useVirtualKeyboard();

  const {
    step,
    duration,
    onApplyButtonClick,
    getBaseRequest,
    getNewPaymentPriceStatus,
    setEmail,
    email,
    onRemovePromoClick,
  } = useStepWithSummary();
  const isCreditCardPayment =
    subscriptionPaymentMethod === SubscriptionPaymentMethod.CREDIT_CARD;
  const renderSummary = !!duration;
  const baseRequest = useMemo(
    () => getBaseRequest(isPromoCodeApplied),
    [isPromoCodeApplied, getBaseRequest]
  );
  const [getPaymentLink, getPaymentLinkStatus] =
    sharedLib.sharedApi.settings.useGetPaymentLinkMutation();

  useLayoutEffect(() => {
    if (!isBottomSummary) {
      scrollToTop();
    }
  }, [step]);

  const openPaymentLink = (link: string) => {
    if (IS_TELEGRAM_WEB_APP) {
      try {
        telegramOpenLink(link);
      } catch {}
    } else {
      window.location.href = link;
    }
  };

  const onGetPaymentLink = () => {
    if (baseRequest) {
      displayResultMessage(
        getPaymentLink({
          ...baseRequest,
          force_invoice: !!localStorage?.getItem(
            LOCAL_STORAGE_FAILED_PAYMENT_KEY
          ),
        }).unwrap()
      )
        .then((response) => {
          if (!isCryptoPayment) {
            openPaymentLink(
              isCreditCardPayment
                ? response?.guest_payment_url
                : response?.payment_url
            );
          }
        })
        .catch(() => {});
    }
  };

  const planForm = duration && <PlanForm />;

  const summary = renderSummary && (
    <Summary
      getPaymentLink={onGetPaymentLink}
      loadingPaymentLink={getPaymentLinkStatus.isLoading}
      setEmail={setEmail}
      email={email}
      onApplyButtonClick={onApplyButtonClick}
      baseRequest={baseRequest}
      getPaymentLinkStatus={getPaymentLinkStatus}
      onRemovePromoClick={onRemovePromoClick}
      getNewPaymentPriceStatus={getNewPaymentPriceStatus}
    />
  );

  const planTitle =
    plan?.subscription_type &&
    `${plan.subscription_type} ${t("plan_form.plan")}`;

  if (isBottomSummary) {
    return (
      <LoadingSpinnerOverlay
        isLoading={isFetchingPaymentMethods}
        hideContentWhileLoading={isFetchingPaymentMethods}
        spinnerSize="large"
      >
        {isNewLook ? (
          <NewLookMobileCheckout
            title={planTitle}
            form={planForm}
            summary={summary}
            hideForm={isKeyboardOpen}
          />
        ) : (
          <FormWithSummaryWrapper $isMobile={true}>
            <MobileSummaryWrapper>{summary}</MobileSummaryWrapper>
            <PlanFormWrapper $hide={isKeyboardOpen}>{planForm}</PlanFormWrapper>
          </FormWithSummaryWrapper>
        )}
      </LoadingSpinnerOverlay>
    );
  }

  if (isNewLook) {
    return (
      <NewLookCheckoutLayout
        title={planTitle}
        onBack={previousStep}
        backLabel={t("cancel_subscription.back_button")}
        form={planForm}
        summary={summary}
      />
    );
  }

  return (
    <FormWithSummaryWrapper>
      {duration && (
        <FormWrapper $isSmallScreen={isSmallScreen}>
          <FormInnerWrapper>{planForm}</FormInnerWrapper>
        </FormWrapper>
      )}
      <SummaryWrapper $isSmallScreen={isSmallScreen}>
        <SummaryInnerWrapper $isSmallScreen={isSmallScreen}>
          {summary}
        </SummaryInnerWrapper>
      </SummaryWrapper>
    </FormWithSummaryWrapper>
  );
};

export default StepWithSummary;

const PlanFormWrapper = styled.div<{ $hide?: boolean }>`
  ${(p) =>
    p.$hide &&
    `
    display: none;
  `};
`;
const MobileSummaryWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;
